/**
 * The building catalogue.
 *
 * A building is either a producer (turns villager labour into resources), a
 * trainer (turns resources into companies), a defence (shoots things), or a
 * comfort (moves the morale needle). Several are more than one.
 *
 * Walls and gates are *not* in this table — they live on the tile layer so they
 * can be drag-placed, auto-tiled and damaged per tile. Their costs are at the
 * bottom of this file.
 */

import { ArmourType, DamageType, Job, Terrain, type ResourceBundle, type ResourceKey } from '../sim/types.ts';

export type BuildingCategory = 'core' | 'economy' | 'military' | 'defence' | 'civic';

/**
 * The silhouette vocabulary.
 *
 * The rule these were chosen under, and the one to keep if you add another:
 * **the shape says what the building does, not what it is made of.** A player
 * at default zoom with the labels off should be able to say "that's the
 * granary" from the outline alone — so a granary is a fat windowless drum, a
 * forge smokes, a barracks has a walled yard and a flag, a tower is thin and
 * twice as tall as anything near it, a mill has sails, a market has awnings.
 *
 * Where two buildings genuinely do the same job — the Smelter, the Runeworks
 * and the Smeltery all melt things — they deliberately share a shape and are
 * told apart by their glow. That is the correct answer: they *are* the same
 * silhouette in the world, and pretending otherwise would spend the vocabulary
 * on distinctions the player does not need to make at a glance.
 */
export type BuildingShape =
  | 'hearth'
  | 'hut'
  | 'hall'
  | 'tower'
  | 'belfry'
  | 'mine'
  | 'farm'
  | 'forge'
  | 'oven'
  | 'yard'
  | 'shrine'
  | 'stable'
  | 'store'
  | 'granary'
  | 'pit'
  | 'field'
  | 'camp'
  | 'sawmill'
  | 'windmill'
  | 'market'
  | 'barracks'
  | 'range'
  | 'well'
  | 'gibbet'
  | 'brazier';

/**
 * The identifying feature, on top of the shape.
 *
 * Twenty-five shapes against eighty-eight buildings meant thirteen of them were
 * a `hall` — a plain box with a crown — and five were a `store`. A tavern, a war
 * room, an academy and a counting house were the same drawing four times, which
 * fails the acceptance test (*name it from its outline, no label*) about as
 * completely as it can be failed.
 *
 * The answer is not thirteen more shapes. It is one feature per building, chosen
 * from a short shared list, **drawn where it breaks the roofline** — because a
 * mark on a wall is invisible at four tiles and a box on a ridge is not. Two
 * buildings may share a shape as long as they do not share a motif, and
 * `tests/buildingart.test.ts` holds that line.
 *
 * The rule for adding one: it must be nameable in a single noun and it must
 * change the outline. "Painted red" is not a motif. "Has a crane on it" is.
 */
export type BuildingMotif =
  | 'none'
  /** One stack, smoke-stained. Somebody lives here. */
  | 'chimney'
  /** A row of them under one roof. Several families do. */
  | 'chimneys'
  /** A board hung off a bracket arm. Somewhere you go in and are served. */
  | 'sign'
  /** A raised clerestory box along the ridge, lit. Somewhere they read. */
  | 'lantern'
  /** A small drum and dome on the ridge. Somewhere they decide. */
  | 'cupola'
  /** A jib and a hook out of the gable end. Goods go up here. */
  | 'crane'
  /** A stack of barrels against the corner. */
  | 'barrels'
  /** Skulls and antlers nailed to the gable. Things are killed here. */
  | 'antlers'
  /** A coiled pipe over a covered vessel. */
  | 'still'
  /** A great toothed wheel on the flank, turning nothing visible. */
  | 'cog'
  /** A beam and two pans on the ridge. Somewhere they count. */
  | 'scales'
  /** Hanging cages, occupied or not. */
  | 'cages'
  /** A rack of tools or spears standing against the wall. */
  | 'rack'
  /** A row of columns across the front. Somewhere the state stands up straight. */
  | 'colonnade'
  /** A ringed sphere on the ridge, turning. Somewhere they are certain about the sky. */
  | 'orrery';

export interface BuildingVisual {
  shape: BuildingShape;
  /** Roof height multiplier — drives the fake-3D silhouette the renderer draws. */
  height: number;
  /** 0-3 chimney/banner/spire clutter level. */
  detail: number;
  banner: boolean;
  glow: number;
  /** The one identifying feature. See `BuildingMotif`. */
  motif: BuildingMotif;
}

export interface TowerAttack {
  damage: number;
  dmgType: number;
  range: number;
  /** Shots per second. */
  rate: number;
  splash: number;
  /** Only fires at ground units if false. */
  hitsFliers: boolean;
}

export interface BuildingType {
  id: string;
  name: string;
  flavour: string;
  category: BuildingCategory;
  /** Age gate: 1 = available from the start. */
  tier: number;
  w: number;
  h: number;
  hp: number;
  cost: Partial<ResourceBundle>;
  /** Builder-seconds of work to finish. */
  buildTime: number;
  /** Villager job slots. */
  workers: number;
  job: number;
  /** Per second at full staffing, before multipliers. */
  produces: Partial<ResourceBundle>;
  /** Consumed per second while producing. */
  consumes: Partial<ResourceBundle>;
  /**
   * The toll: seconds of full-rate work per head taken off the shift. 0 for
   * everything that is not the Hollow.
   *
   * This is the whole of what the fifth Age *is*, so it is worth stating here
   * rather than four buildings down. Every other converter in the game turns
   * goods into better goods; a Hollow converter turns goods and a person into
   * better goods. The toll is charged against the same effective rate the recipe
   * is — a yard running at half staffing through an input shortage takes half as
   * long to owe a head as one running flat out — and it is paid out of *its own
   * shift*, the nearest worker to hand being the one already standing in the
   * doorway.
   *
   * Two consequences, both deliberate. A Hollowing de-staffs itself as it works,
   * so its output curves down toward whatever rate the town can replace people
   * at; and a settlement that wants two of them has to be a settlement that
   * feeds and houses a surplus it is knowingly spending. Nothing here is free,
   * and the thing it is not free in is not a bar on the strip.
   *
   * Accounted in `b.stock`, which is hashed and saved already and which nothing
   * but the Hearth's own pop-growth counter has ever used.
   */
  toll: number;
  /** Footprint must sit on one of these terrains (any tile), if set. */
  needsTerrain?: number[];
  /**
   * Footprint must have one of these terrains within a tile of it, if set.
   *
   * The distinction from `needsTerrain` is the whole reason it exists: a weir is
   * built on the **bank**, not in the river. Water is the one terrain in the
   * game that `canBuild` refuses outright, so a fishery expressed as
   * `needsTerrain: [River]` would be a building that can never legally be
   * placed anywhere. Checked in `canPlaceBuilding` beside its sibling.
   */
  needsNearTerrain?: number[];
  /**
   * What an upgrade is priced off, when that is not the build cost.
   *
   * `doUpgradeBuilding` scales `cost` by the tier, which works for everything a
   * player pays to raise in the first place and gives exactly the wrong answer
   * for the one building they do not: **the Hearth is placed for free at the
   * start of the run, so its `cost` is empty, so every one of its four upgrades
   * was also free** — twelve population, two hundred storage and a tier of
   * hitpoints, four times over, for nothing. That is the whole of the owner's
   * *"hearth upgrade should not be free"*, and it was not a missing price so
   * much as a price derived from the wrong number.
   */
  upgradeCost?: Partial<ResourceBundle>;
  popCap: number;
  morale: number;
  /** Storage added to the resource caps. */
  storage: number;
  /**
   * Mana capacity this building adds, scaled by tier exactly as `popCap` is.
   *
   * The fourth of the owner's four Mana sources and the only one that is a
   * *building*, which is what makes the undead's binding a thing you lay out on
   * the ground rather than a number in a menu. Nonzero on two buildings in the
   * catalogue and read by one race; see `recomputeManaCap` in `world.ts` for why
   * it is computed for everybody regardless.
   *
   * A capacity, not a stock — nothing is stored here and nothing is spent. A
   * race bound by Mana holds as many models standing as the pool is wide.
   */
  mana: number;
  /**
   * Which race may raise this at all, or '' for the whole catalogue.
   *
   * The gate is checked in `doBuild` and again when the build menu is assembled,
   * and it is deliberately a single id rather than a list: a building two peoples
   * can raise is a building neither of them is identified by. Where two races
   * genuinely want the same shed, they get two entries with two names, which
   * costs a dozen lines and buys the thing the whole pass is for.
   */
  race: string;
  /**
   * The tech node that unlocks this, or absent for the catalogue's default.
   *
   * Data, not a special case in `availableBuildings` — which is where the Soul
   * Furnace's gate lived, checking a node id that did not exist, so the one
   * building the Sacrament Furnace node claimed to unlock was unbuildable for
   * everyone forever and no test could see it. A field here is greppable, the
   * filter reads it generically, and `tests/tech.test.ts` walks every gate and
   * asserts the node it names is real.
   */
  tech?: string;
  /**
   * This building must be unlocked by an Academy node before it may be raised.
   *
   * Separate from `race` because the two answer different questions and a player
   * deserves the right refusal for each: `race` is "not ours" and is permanent,
   * this is "not learned" and is a thing to go and do. It is also why every race
   * gets *one* building it may raise from the first wave and one it has to earn
   * — the first is the identity and the second is the reward for the column.
   */
  researched: boolean;
  attack?: TowerAttack;
  /** Unit ids this building lets you raise. */
  trains: string[];
  /** Powers this building unlocks. */
  grants: string[];
  /** Max upgrade tier (faction bonus can raise this). */
  maxTier: number;
  /**
   * May a *village* raise this?
   *
   * > *"villages, not cities, should have their own set of buildings separate to
   * > the city — they should be bonus material for the city, not full cities
   * > themselves"*
   *
   * A holding below `VILLAGE_RUNG` gets the short catalogue: the ground, the
   * roofs over the people who work it, and the four earthworks that keep a
   * raiding party out. No barracks, no academy, no treasury, no seat. That is
   * what makes a village *feed* a city rather than compete with it, and it is
   * why a crown with six holdings is a crown with one capital and five estates
   * rather than six identical towns at six different sizes.
   *
   * **Derived rather than typed out ninety-six times** — see `villageMay`
   * below. A rule means a race building added tomorrow lands in the right
   * catalogue with nothing written here, which is the same argument
   * `raiseTown` makes for reading `BuildingType.race` instead of keeping a
   * table of ids per people.
   */
  village: boolean;
  vis: BuildingVisual;
}

/**
 * The two hands the rule below needs, and why they are lists of ids when the
 * rest of it is a rule.
 *
 * The rule is "raw production, plus somewhere to live" and it is right about
 * ninety of the ninety-six. What it cannot see is the difference between a
 * **counting house** and a **quarry** — both are `economy`, both are early,
 * and only one of them is a thing a hamlet has. So the exceptions are named,
 * and they are named rather than encoded as a fifth category because there are
 * eleven of them and a category with eleven members that exists to carve out
 * eleven members is not a category.
 */
const VILLAGE_ALSO = new Set([
  // Roofs, and the one building that keeps the people under them from despair.
  'house',
  'longhouse',
  'chapel',
  // What a village puts up when it hears horses. Earth, sharpened wood and one
  // man with a horn — not a tower with a garrison in it.
  'trench',
  'caltrop_field',
  'watchtower',
  'brazier',
]);

/**
 * A city's institutions, which happen to be filed under `economy`.
 *
 * The treasury and the vault are what a *capital* does with money. A village
 * that could raise them would be a village with a mint in it, and the whole
 * point of the short catalogue is that the wealth leaves for the city.
 */
const VILLAGE_NEVER = new Set(['counting_house', 'vault']);

/**
 * The rule: a village works the ground and houses the people who work it.
 *
 * `trains` is the sharpest line in the catalogue and it is checked first — a
 * building that raises a company is a thing a *town* has, whatever else it
 * does, which is what keeps the Cog Works and the Bloodcourt out without
 * naming them. Everything else is early economy, because a village is the
 * first two Ages of production and nothing after them: the Soul Furnace and
 * the Ashworks are an industry, and an industry needs a city around it.
 */
function villageMay(spec: BSpec, tier: number, trains: readonly string[]): boolean {
  if (VILLAGE_NEVER.has(spec.id)) return false;
  if (VILLAGE_ALSO.has(spec.id)) return true;
  if (trains.length > 0) return false;
  return spec.category === 'economy' && tier <= 2;
}

type BSpec = Omit<Partial<BuildingType>, 'vis'> & {
  id: string;
  name: string;
  flavour: string;
  category: BuildingCategory;
  vis: Partial<BuildingVisual> & { shape: BuildingShape };
};

function b(spec: BSpec): BuildingType {
  return {
    id: spec.id,
    name: spec.name,
    flavour: spec.flavour,
    category: spec.category,
    tier: spec.tier ?? 1,
    w: spec.w ?? 2,
    h: spec.h ?? 2,
    hp: spec.hp ?? 400,
    cost: spec.cost ?? {},
    buildTime: spec.buildTime ?? 12,
    workers: spec.workers ?? 0,
    job: spec.job ?? Job.Idle,
    produces: spec.produces ?? {},
    consumes: spec.consumes ?? {},
    toll: spec.toll ?? 0,
    needsTerrain: spec.needsTerrain,
    needsNearTerrain: spec.needsNearTerrain,
    upgradeCost: spec.upgradeCost,
    popCap: spec.popCap ?? 0,
    morale: spec.morale ?? 0,
    storage: spec.storage ?? 0,
    mana: spec.mana ?? 0,
    race: spec.race ?? '',
    tech: spec.tech,
    researched: spec.researched ?? false,
    attack: spec.attack,
    trains: spec.trains ?? [],
    grants: spec.grants ?? [],
    maxTier: spec.maxTier ?? 3,
    village: spec.village ?? villageMay(spec, spec.tier ?? 1, spec.trains ?? []),
    vis: {
      shape: spec.vis.shape,
      height: spec.vis.height ?? 1,
      detail: spec.vis.detail ?? 1,
      banner: spec.vis.banner ?? false,
      glow: spec.vis.glow ?? 0,
      motif: spec.vis.motif ?? 'none',
    },
  };
}

export const BUILDING_TYPES: BuildingType[] = [
  // ── Core ─────────────────────────────────────────────────────────────────
  b({
    id: 'hearth',
    name: 'The Hearth',
    flavour: 'It has not gone out. That is the whole of the realm’s good news.',
    category: 'core',
    w: 3,
    h: 3,
    hp: 4000,
    buildTime: 0,
    // The Hearth alone has to house the opening villagers *and* leave room to
    // raise a first company. At 12 it did neither, and a settlement that lost
    // its houses could never field an army again.
    /**
     * Beds for the founding party, **and room over it**.
     *
     * Cut from 26 with the rest of the housing, and 12 was wrong: `initRun`
     * seeds exactly twelve villagers, so a hall with twelve beds is a town that
     * is full on tick zero. It cannot grow, and `musterRoom` refuses every
     * company because taking anyone would empty the place — which
     * `tests/companies-size.test.ts` caught by name.
     *
     * Eighteen is the founding party and half again. The Hearth is the hall at
     * the middle of the town rather than a dwelling, so it is the one building
     * that is allowed to sleep more than a family; everything that is actually
     * a house is three or four.
     */
    popCap: 18,
    morale: 0.25,
    storage: 400,
    maxTier: 5,
    /**
     * Four upgrades that used to be free, at 0.85 × tier + 10 apiece:
     * 180/146/129, 350/282/248, 520/418/367, 690/554/486 of timber, stone and
     * coin. Priced against what each one *gives* — thirteen more beds, two
     * hundred more shed and a tier of stone — and deliberately steep enough at
     * the top that raising the Hearth to five is an alternative to an Age
     * rather than a step on the way to one. It is both, in fact: see
     * `AGE_HEARTH_TIER`.
     */
    upgradeCost: { timber: 200, stone: 160, gold: 140 },
    /**
     * Starfall was here and is gone. See `RETIRED_POWERS` in `data/powers.ts`
     * for why the whole direct-damage family went; what the Hearth grants
     * instead is a power that moves the enemy rather than deleting it, which is
     * the shape the survivors all have.
     */
    grants: ['haul_down'],
    vis: { shape: 'hearth', height: 1.6, detail: 3, banner: true, glow: 1, motif: 'none' },
  }),

  // ── Economy ──────────────────────────────────────────────────────────────
  b({
    id: 'woodcutter',
    name: 'Woodcutter’s Camp',
    flavour: 'The trees died long before the men did, which has made the work quieter.',
    category: 'economy',
    w: 2,
    h: 2,
    hp: 260,
    cost: { timber: 25 },
    buildTime: 9,
    workers: 3,
    job: Job.Woodcutter,
    // One camp must out-produce one sawmill, or the chain nets to zero timber
    // and nothing downstream of it can ever be paid for. Trimmed from 0.5 once
    // the sweep showed timber running nineteen to one against stone.
    produces: { timber: 0.42 },
    vis: { shape: 'camp', height: 0.8, detail: 1, motif: 'none' },
  }),
  b({
    id: 'sawmill',
    name: 'Sawmill',
    flavour: 'Two blades, one river, and a long-standing arrangement about fingers.',
    category: 'economy',
    w: 3,
    h: 2,
    hp: 380,
    cost: { timber: 70, stone: 20 },
    buildTime: 22,
    workers: 4,
    job: Job.Sawyer,
    produces: { planks: 0.24 },
    consumes: { timber: 0.3 },
    storage: 150,
    vis: { shape: 'sawmill', height: 1.15, detail: 2, motif: 'none' },
  }),
  b({
    id: 'bakery',
    name: 'Bakery',
    flavour: 'The only queue in the settlement that people join willingly.',
    category: 'economy',
    w: 2,
    h: 2,
    hp: 300,
    cost: { timber: 40, stone: 30 },
    buildTime: 16,
    workers: 2,
    job: Job.Baker,
    produces: { bread: 0.2 },
    consumes: { grain: 0.24, timber: 0.04 },
    morale: 0.05,
    storage: 100,
    vis: { shape: 'oven', height: 1.0, detail: 2, glow: 0.6, motif: 'chimney' },
  }),
  b({
    id: 'market',
    name: 'Marketplace',
    flavour: 'Four stalls and a rumour. The rumour is the busiest of the five.',
    category: 'economy',
    w: 4,
    h: 4,
    hp: 260,
    cost: { timber: 70, planks: 20 },
    buildTime: 20,
    workers: 2,
    job: Job.Carter,
    morale: 0.1,
    storage: 250,
    // Coin, out of nothing but people standing near each other with things to
    // sell. The first building that pays a wage bill rather than adding to it.
    produces: { gold: 0.34 },
    vis: { shape: 'market', height: 0.62, detail: 3, banner: true, motif: 'none' },
  }),
  b({
    id: 'counting_house',
    name: 'Counting House',
    flavour:
      'Ledgers in a locked room. The realm may be ending, but it will be ending in a documented fashion.',
    category: 'economy',
    tier: 2,
    w: 3,
    h: 3,
    hp: 420,
    cost: { timber: 60, planks: 40, stone: 50, gold: 60 },
    buildTime: 26,
    workers: 3,
    job: Job.Carter,
    // Grain and planks go out on the road; coin comes back. This is the only
    // conversion in the game that turns surplus *anything* into wages.
    consumes: { grain: 0.18, planks: 0.06 },
    produces: { gold: 0.82 },
    storage: 200,
    morale: -0.04,
    vis: { shape: 'hall', height: 1.35, detail: 3, banner: true, motif: 'scales' },
  }),
  b({
    id: 'storehouse',
    name: 'Storehouse',
    flavour: 'Everything that is not currently being carried somewhere.',
    category: 'economy',
    w: 3,
    h: 2,
    hp: 340,
    cost: { timber: 55, planks: 10 },
    buildTime: 14,
    storage: 450,
    vis: { shape: 'store', height: 0.95, detail: 2, motif: 'crane' },
  }),
  b({
    id: 'quarry',
    name: 'Quarry',
    flavour: 'They cut the stone in the shape of the hole it leaves.',
    category: 'economy',
    w: 2,
    h: 2,
    hp: 300,
    cost: { timber: 35 },
    buildTime: 11,
    workers: 3,
    job: Job.Quarrier,
    // Stone is the defence material and it was the run-limiting one: a 24-run
    // sweep produced 42,000 timber against 2,200 stone, so walls simply never
    // got built. Raised until a settlement can actually afford its own ring.
    produces: { stone: 0.46 },
    vis: { shape: 'pit', height: 0.7, detail: 1, motif: 'crane' },
  }),
  b({
    id: 'iron_mine',
    name: 'Iron Mine',
    flavour: 'Down there the dark is at least an honest dark.',
    category: 'economy',
    w: 2,
    h: 2,
    hp: 320,
    cost: { timber: 45, stone: 25 },
    buildTime: 14,
    workers: 3,
    job: Job.Miner,
    produces: { iron: 0.2 },
    vis: { shape: 'mine', height: 0.9, detail: 2, motif: 'none' },
  }),
  b({
    id: 'deep_mine',
    name: 'Deep Mine',
    flavour: 'Sunk past the point where the shifts are named after which meal you miss.',
    category: 'economy',
    tier: 3,
    w: 3,
    h: 3,
    hp: 520,
    cost: { timber: 80, planks: 30, stone: 90, iron: 30 },
    buildTime: 30,
    workers: 6,
    job: Job.Miner,
    produces: { iron: 0.62, stone: 0.18 },
    vis: { shape: 'mine', height: 1.2, detail: 3, motif: 'crane' },
  }),
  b({
    id: 'farm',
    name: 'Ashfield Farm',
    flavour: 'Grain grown in ash tastes of ash. It is still grain.',
    category: 'economy',
    w: 3,
    h: 3,
    hp: 200,
    cost: { timber: 30 },
    buildTime: 10,
    workers: 4,
    job: Job.Farmer,
    // Grain sits under everything — population upkeep, the bakery, and every
    // company's muster cost. One farm has to comfortably feed more than it eats.
    produces: { grain: 0.72 },
    vis: { shape: 'farm', height: 0.7, detail: 2, motif: 'none' },
  }),
  /**
   * The other two foods.
   *
   * The owner's ask was *"have different types of food like fish etc"*, and the
   * point of a second and third kind is not more bars — it is that food stops
   * being one building repeated. A farm stands anywhere; these two stand
   * somewhere in particular, so feeding a city is a claim on ground rather than
   * a line item, and `FOOD_VALUE` makes each of them worth a different amount of
   * appetite. See `stepUpkeep` in `systems/settlement.ts` for the larder.
   */
  b({
    id: 'fishery',
    name: 'Weir and Nets',
    flavour: 'The river brought them. Nobody is asking what the river was doing upstream.',
    category: 'economy',
    w: 2,
    h: 2,
    hp: 200,
    cost: { timber: 45 },
    buildTime: 12,
    workers: 3,
    job: Job.Fisher,
    /**
     * Rated below a farm per head and above it per acre. A farm is 0.72 grain
     * off nine tiles and four hands; a weir is 0.34 fish — 0.48 grain-equivalent
     * — off four tiles and three. What it actually buys is *independence from
     * the fields*: fish is the one food a Famine Wind cannot stop and the one
     * that does not compete with the bakery for grain.
     */
    produces: { fish: 0.34 },
    /**
     * On the bank, never in the channel. Every seed has a real river since the
     * water pass, with deep water down the middle and fordable margins, and
     * until now nothing in the catalogue had any reason to care where it was.
     */
    needsNearTerrain: [Terrain.River, Terrain.Deep],
    storage: 60,
    // A jib and a hook: the nets come up on it. Distinct from the camp beside
    // it in the deadwood, which the art rule in `tests/townart.test.ts` insists
    // on and which the eye insists on first.
    vis: { shape: 'camp', height: 0.7, detail: 2, motif: 'crane' },
  }),
  b({
    id: 'hunters_lodge',
    name: 'Hunters’ Lodge',
    flavour: 'What is left in the deadwood is not good eating. It is eating.',
    category: 'economy',
    w: 2,
    h: 2,
    hp: 220,
    cost: { timber: 55, iron: 10 },
    buildTime: 14,
    workers: 3,
    job: Job.Hunter,
    /**
     * Salt meat is worth twice grain, so 0.19 a second is 0.38 grain-equivalent
     * — the thinnest yield of the three foods and the richest per unit, which
     * is what makes it the diet's *quality* rather than its bulk.
     */
    produces: { meat: 0.19 },
    consumes: { timber: 0.02 },
    /**
     * Standing on the same deadwood a woodcutter's camp wants, and that tension
     * is the decision: the lodge does not fell the trees (`harvestKind` is keyed
     * on the job, and this one hauls nothing), but it is standing on ground a
     * camp could have had.
     */
    needsTerrain: [Terrain.Forest],
    morale: 0.03,
    storage: 60,
    // Skulls and antlers on the gable. Things are killed here.
    vis: { shape: 'camp', height: 0.9, detail: 2, motif: 'antlers' },
  }),
  b({
    id: 'mill',
    name: 'Mill',
    flavour: 'Turns whether or not there is wind. Nobody asks about this.',
    category: 'economy',
    tier: 2,
    w: 2,
    h: 2,
    hp: 300,
    cost: { timber: 60, stone: 20 },
    buildTime: 15,
    workers: 2,
    job: Job.Farmer,
    produces: { grain: 0.55 },
    storage: 120,
    vis: { shape: 'windmill', height: 1.7, detail: 2, motif: 'none' },
  }),
  b({
    id: 'granary',
    name: 'Granary',
    flavour: 'Full in autumn. Counted every night thereafter.',
    category: 'economy',
    w: 2,
    h: 2,
    hp: 340,
    cost: { timber: 45, stone: 15 },
    buildTime: 12,
    storage: 300,
    morale: 0.04,
    vis: { shape: 'granary', height: 1.35, detail: 1, motif: 'crane' },
  }),
  b({
    id: 'smelter',
    name: 'Smelter',
    flavour: 'The only building in the settlement that is reliably warm.',
    category: 'economy',
    tier: 2,
    w: 3,
    h: 2,
    hp: 430,
    cost: { stone: 70, timber: 40 },
    buildTime: 18,
    workers: 3,
    job: Job.Smith,
    produces: { iron: 0.34 },
    consumes: { timber: 0.16 },
    vis: { shape: 'forge', height: 1.2, detail: 3, glow: 0.7, motif: 'chimneys' },
  }),
  b({
    id: 'workshop',
    name: 'Workshop',
    flavour: 'Where good ideas are turned into slightly worse objects.',
    category: 'economy',
    tier: 2,
    w: 3,
    h: 2,
    hp: 400,
    cost: { timber: 70, planks: 35, iron: 30 },
    buildTime: 24,
    workers: 3,
    job: Job.Smith,
    produces: { planks: 0.1 },
    consumes: { timber: 0.1 },
    vis: { shape: 'hall', height: 1.0, detail: 3, motif: 'cog' },
  }),
  // ── The Kindling: the second tier of the chain ───────────────────────────
  //
  // Four buildings, and the rule they were written under is worth stating once
  // here rather than four times below: **they consume the first Age's goods
  // rather than replacing them.** An Arcane Sawmill eats plain timber, so the
  // right answer to owning one is more woodcutters, not fewer. Nothing in this
  // block makes anything above it obsolete.
  //
  // All four are `tier: 2`, so the Age gate in `canPlaceBuilding` handles them
  // and no new gating code exists anywhere.
  b({
    id: 'wellspring',
    name: 'Wellspring',
    flavour: 'The ground here has been humming since before the Hearth was lit. Someone finally brought a bucket.',
    category: 'economy',
    tier: 2,
    w: 2,
    h: 2,
    hp: 340,
    cost: { timber: 60, stone: 80, gold: 40 },
    buildTime: 24,
    workers: 3,
    job: Job.Acolyte,
    // The only source of essence there is, and it must stand on a leyline —
    // which is out past the safe bowl with the forests and the seams. The
    // second Age is bought by widening the perimeter, like the first one was.
    produces: { essence: 0.16 },
    needsTerrain: [Terrain.Leyline],
    morale: -0.03,
    vis: { shape: 'well', height: 1.1, detail: 3, glow: 1, motif: 'none' },
  }),
  b({
    id: 'arcane_sawmill',
    name: 'Arcane Sawmill',
    flavour: 'Same two blades. Same river. The wood has started agreeing to it in advance.',
    category: 'economy',
    tier: 2,
    w: 3,
    h: 2,
    hp: 420,
    cost: { timber: 90, planks: 40, stone: 30, gold: 30 },
    buildTime: 26,
    workers: 4,
    job: Job.Sawyer,
    // Two timber and one essence per magewood. The timber is the point.
    produces: { magewood: 0.09 },
    consumes: { timber: 0.18, essence: 0.09 },
    storage: 120,
    vis: { shape: 'sawmill', height: 1.2, detail: 3, glow: 0.5, motif: 'orrery' },
  }),
  b({
    id: 'runeworks',
    name: 'Runeworks',
    flavour: 'They cut the stone in the shape of the hole it leaves, then teach it to remember the shape.',
    category: 'economy',
    tier: 2,
    w: 3,
    h: 2,
    hp: 460,
    cost: { stone: 110, planks: 30, iron: 20, gold: 30 },
    buildTime: 28,
    workers: 4,
    job: Job.Smith,
    produces: { runestone: 0.09 },
    consumes: { stone: 0.18, essence: 0.09 },
    storage: 120,
    vis: { shape: 'forge', height: 1.15, detail: 3, glow: 0.5, motif: 'none' },
  }),
  b({
    id: 'smeltery',
    name: 'Smeltery',
    flavour: 'Warmer than the Smelter, and considerably less popular to work in.',
    category: 'economy',
    tier: 2,
    w: 3,
    h: 3,
    hp: 480,
    cost: { stone: 90, timber: 70, iron: 40 },
    buildTime: 26,
    workers: 4,
    job: Job.Smith,
    // The one Age-2 good with no magic in it: iron and a great deal of fuel.
    // Which means a settlement that never finds a leyline can still make one of
    // the four, and is never wholly locked out of the tier.
    produces: { ingots: 0.11 },
    consumes: { iron: 0.22, charcoal: 0.08 },
    morale: -0.04,
    vis: { shape: 'forge', height: 1.35, detail: 3, glow: 0.9, motif: 'chimney' },
  }),

  b({
    id: 'soul_furnace',
    tech: 'sac_furnace',
    name: 'Soul Furnace',
    flavour: 'It renders what the dead no longer need into something the living do.',
    category: 'economy',
    tier: 3,
    w: 3,
    h: 3,
    hp: 560,
    cost: { stone: 120, iron: 70, souls: 40 },
    buildTime: 26,
    workers: 3,
    job: Job.Acolyte,
    produces: { souls: 0.16 },
    morale: -0.06,
    storage: 200,
    vis: { shape: 'forge', height: 1.5, detail: 3, glow: 1, motif: 'cages' },
  }),

  // ── The Bell: the third tier ─────────────────────────────────────────────
  //
  // Same rule as the Kindling and it is the only rule that matters: **every one
  // of these eats the Age below.** Soulwood is magewood, voidstone is runestone,
  // alloy is ingots; so a settlement that reaches the Bell does not retire its
  // Arcane Sawmill, it builds a second one and a Grafting House behind it, and
  // the woodcutters that started the whole line are worth more on the day the
  // third Age turns than they were the day before.
  //
  // What is new is the *other* input. Three of the four want Souls, which have
  // until now been a currency you spent on powers and never a material you
  // spent on goods. That is the Bell: the graveyard stops being somewhere you
  // keep people and becomes somewhere you draw on, and every Corpse Pyre and
  // Soul Furnace in the settlement is suddenly load-bearing.
  //
  // The essence bill is worth reading as a block. A Wellspring makes 0.16/s; a
  // Void Kiln alone drinks 0.10 and a Distillery 0.16. The Bell is bought by
  // widening the perimeter *again*, out to leylines the second Age never needed.
  b({
    id: 'grafting_house',
    name: 'Grafting House',
    flavour: 'The wood takes. That is the word they use, and they use it about the wood.',
    category: 'economy',
    tier: 3,
    w: 3,
    h: 3,
    hp: 520,
    cost: { magewood: 60, runestone: 40, planks: 60, souls: 40 },
    buildTime: 32,
    workers: 5,
    job: Job.Sawyer,
    produces: { soulwood: 0.05 },
    consumes: { magewood: 0.1, souls: 0.05 },
    storage: 120,
    morale: -0.05,
    vis: { shape: 'sawmill', height: 1.3, detail: 3, glow: 0.7, motif: 'still' },
  }),
  b({
    id: 'void_kiln',
    name: 'Void Kiln',
    flavour: 'Fired until the pattern comes out of it. What is left weighs more and says less.',
    category: 'economy',
    tier: 3,
    w: 3,
    h: 3,
    hp: 560,
    cost: { runestone: 90, ingots: 40, stone: 120 },
    buildTime: 34,
    workers: 5,
    job: Job.Smith,
    produces: { voidstone: 0.05 },
    consumes: { runestone: 0.1, essence: 0.1 },
    storage: 120,
    vis: { shape: 'forge', height: 1.4, detail: 3, glow: 0.4, motif: 'cupola' },
  }),
  b({
    id: 'rune_forge',
    name: 'Rune Forge',
    flavour: 'Two metals that do not like each other, held together by a third opinion.',
    category: 'economy',
    tier: 3,
    w: 3,
    h: 3,
    hp: 580,
    cost: { ingots: 80, runestone: 50, stone: 100 },
    buildTime: 34,
    workers: 5,
    job: Job.Smith,
    produces: { alloy: 0.05 },
    consumes: { ingots: 0.1, runestone: 0.05 },
    storage: 120,
    morale: -0.04,
    vis: { shape: 'forge', height: 1.45, detail: 3, glow: 0.95, motif: 'rack' },
  }),
  b({
    id: 'distillery',
    name: 'Distillery',
    flavour: 'Forty gallons in, a thimble out, and the thimble is the part that is dangerous.',
    category: 'economy',
    tier: 3,
    w: 2,
    h: 3,
    hp: 460,
    cost: { magewood: 50, runestone: 50, ingots: 40, gold: 120 },
    buildTime: 30,
    workers: 5,
    job: Job.Acolyte,
    // The thinnest yield in the game and the widest bill: four essence and two
    // souls for one measure. It is priced that way because quintessence is what
    // both of the Gilded's converters run on, so a settlement that wants the
    // fourth Age needs a *row* of these and the leylines to feed them.
    produces: { quintessence: 0.04 },
    consumes: { essence: 0.16, souls: 0.08 },
    storage: 100,
    morale: -0.06,
    vis: { shape: 'oven', height: 1.5, detail: 3, glow: 0.8, motif: 'still' },
  }),
  b({
    id: 'vault',
    tech: 'fort_deep_cellars',
    name: 'The Deep Vault',
    flavour: 'Counted twice on the way in and once, quietly, on the way out.',
    category: 'economy',
    tier: 3,
    w: 4,
    h: 3,
    hp: 900,
    cost: { stone: 200, planks: 80, ingots: 60 },
    buildTime: 36,
    // By the Bell a settlement is running three generations of production and
    // the binding constraint stops being what it can make and becomes where it
    // can put it — an Age price in three figures has to fit in a shed before it
    // can be paid. This is the answer, and it is deliberately not cheap.
    storage: 900,
    vis: { shape: 'store', height: 1.25, detail: 3, motif: 'cupola' },
  }),
  b({
    id: 'stormcaller_yard',
    name: 'Stormcaller Yard',
    flavour: 'Sigil frames on one bench, focusing rings on the other, and a rope between them nobody crosses.',
    category: 'military',
    tier: 3,
    w: 4,
    h: 3,
    hp: 620,
    cost: { soulwood: 60, alloy: 50, planks: 80, iron: 60 },
    buildTime: 38,
    popCap: 4,
    // Assembly consumes finished parts supplied by the specialist workshops.
    trains: ['siege', 'caster'],
    vis: { shape: 'yard', height: 1.1, detail: 3, banner: true, motif: 'orrery' },
  }),
  b({
    id: 'voidspire',
    name: 'Voidspire',
    flavour: 'It does not fire. Something in front of it simply stops having been there.',
    category: 'defence',
    tier: 3,
    w: 2,
    h: 2,
    hp: 900,
    cost: { voidstone: 80, alloy: 40, stone: 90 },
    buildTime: 34,
    attack: { damage: 46, dmgType: DamageType.Arcane, range: 9.2, rate: 0.65, splash: 0.9, hitsFliers: true },
    vis: { shape: 'tower', height: 2.9, detail: 3, glow: 1, motif: 'cages' },
  }),

  // ── The Gilded: the fourth tier ──────────────────────────────────────────
  //
  // Two converters, not four, and the reason is ground rather than recipes. By
  // here a settlement is running four generations at once on a map that has not
  // grown, so the fourth Age's contribution is *depth* — both of these eat two
  // Bell goods apiece, which means every Ashworks is a demand for a Void Kiln
  // and two Distilleries standing behind it, and each of those is a demand for
  // a Runeworks and a pair of Wellsprings behind *them*. Four buildings deep,
  // and the woodcutter at the bottom is still cutting.
  b({
    id: 'ashworks',
    name: 'Ashworks',
    flavour: 'Drawn into sheet while it is still deciding. It never breaks; it only stops.',
    category: 'economy',
    tier: 4,
    w: 4,
    h: 3,
    hp: 700,
    cost: { voidstone: 90, alloy: 60, runestone: 80, gold: 200 },
    buildTime: 44,
    workers: 6,
    job: Job.Smith,
    produces: { ashglass: 0.03 },
    consumes: { voidstone: 0.06, quintessence: 0.03 },
    storage: 150,
    vis: { shape: 'forge', height: 1.55, detail: 3, glow: 0.5, motif: 'barrels' },
  }),
  b({
    id: 'great_forge',
    name: 'The Great Forge',
    flavour: 'Warm to the touch on the outside. They have stopped pretending that is the fire.',
    category: 'economy',
    tier: 4,
    w: 4,
    h: 4,
    hp: 760,
    cost: { alloy: 100, soulwood: 50, stone: 160, gold: 200 },
    buildTime: 46,
    workers: 6,
    job: Job.Smith,
    produces: { heartiron: 0.03 },
    consumes: { alloy: 0.06, quintessence: 0.03 },
    storage: 150,
    morale: -0.07,
    vis: { shape: 'forge', height: 1.75, detail: 3, glow: 1, motif: 'crane' },
  }),
  b({
    id: 'gilded_yard',
    name: 'Gilded Yard',
    flavour: 'Everything leaves here plated. It does not help, and it is not for them.',
    category: 'military',
    tier: 4,
    w: 4,
    h: 4,
    hp: 780,
    cost: { heartiron: 60, ashglass: 40, alloy: 80 },
    buildTime: 46,
    popCap: 6,
    trains: ['siege', 'militia', 'archer'],
    morale: 0.04,
    vis: { shape: 'barracks', height: 1.35, detail: 3, banner: true, glow: 0.4, motif: 'colonnade' },
  }),
  b({
    id: 'gilded_hall',
    name: 'The Gilded Hall',
    flavour: 'Built for a court that will not be coming. It sleeps two hundred, which is the point.',
    category: 'civic',
    tier: 4,
    w: 5,
    h: 4,
    hp: 820,
    cost: { ashglass: 15, heartiron: 10, gold: 100 },
    buildTime: 48,
    // The city building. A settlement that means to field an Age-4 army needs
    // beds on a different order from the ones a hovel provides, and this is the
    // first thing in the catalogue that supplies them.
    popCap: 11,
    morale: 0.12,
    consumes: { bread: 0.05 },
    vis: { shape: 'hall', height: 1.6, detail: 3, banner: true, glow: 0.6, motif: 'colonnade' },
  }),
  b({
    id: 'gilded_bastion',
    name: 'Gilded Bastion',
    flavour: 'The gold is a quarter-inch thick and does nothing. The eleven feet behind it do.',
    category: 'defence',
    tier: 4,
    w: 3,
    h: 3,
    hp: 2200,
    cost: { heartiron: 70, ashglass: 50, stone: 180 },
    buildTime: 44,
    attack: { damage: 96, dmgType: DamageType.Siege, range: 10.5, rate: 0.3, splash: 1.3, hitsFliers: true },
    vis: { shape: 'tower', height: 2.0, detail: 3, banner: true, glow: 0.5, motif: 'colonnade' },
  }),

  // ── The Hollow: the fifth tier, and the price of it ──────────────────────
  //
  // Two converters and both of them carry a `toll`. Read the note at the field
  // in `BuildingType` before changing a number here: these are the only two
  // buildings in the game that consume people, they take them off their own
  // shift, and that is the entire design of the Age rather than a garnish on it.
  //
  // The accumulate rule holds all the way to the top — hollowglass is ashglass,
  // gravesilver is heartiron — so the Hollow does not retire the Gilded any more
  // than the Gilded retired the Bell. What it does is put a fifth demand under
  // the same woodcutter, and charge for the privilege in the one currency the
  // settlement cannot mine.
  b({
    id: 'the_hollowing',
    name: 'The Hollowing',
    flavour: 'The shift is nine. It has been nine since it opened, and it has never been the same nine.',
    category: 'economy',
    tier: 5,
    w: 4,
    h: 4,
    hp: 820,
    cost: { ashglass: 70, heartiron: 40, souls: 200, gold: 400 },
    buildTime: 54,
    workers: 6,
    job: Job.Acolyte,
    produces: { hollowglass: 0.02 },
    consumes: { ashglass: 0.04, souls: 0.1 },
    // A head every hundred and ten seconds of flat-out work. A well-fed town
    // makes a villager about every fourteen, so one of these is an inconvenience
    // and six of them are a decision about what the settlement is for.
    toll: 110,
    storage: 150,
    morale: -0.14,
    vis: { shape: 'pit', height: 1.1, detail: 3, glow: 0.35, motif: 'cages' },
  }),
  b({
    id: 'mourning_forge',
    name: 'Mourning Forge',
    flavour: 'It rings for about a day after each pour. They have stopped counting which.',
    category: 'economy',
    tier: 5,
    w: 4,
    h: 4,
    hp: 860,
    cost: { heartiron: 70, ashglass: 40, souls: 200, gold: 400 },
    buildTime: 54,
    workers: 6,
    job: Job.Smith,
    produces: { gravesilver: 0.02 },
    consumes: { heartiron: 0.04, souls: 0.1 },
    toll: 110,
    storage: 150,
    morale: -0.14,
    vis: { shape: 'forge', height: 1.8, detail: 3, glow: 0.8, motif: 'orrery' },
  }),
  b({
    id: 'hollow_gate',
    name: 'The Hollow Gate',
    flavour: 'They walk in through the near arch. What comes out of the far one is punctual.',
    category: 'military',
    tier: 5,
    w: 4,
    h: 3,
    hp: 900,
    cost: { hollowglass: 60, gravesilver: 60, souls: 250 },
    buildTime: 52,
    popCap: 7,
    trains: ['militia', 'siege'],
    morale: -0.1,
    vis: { shape: 'belfry', height: 2.4, detail: 3, banner: true, glow: 0.3, motif: 'colonnade' },
  }),
  b({
    id: 'hollow_crown',
    name: 'The Hollow Crown',
    flavour: 'A city can be governed from a smaller room. It cannot be governed this long from one.',
    category: 'civic',
    tier: 5,
    w: 5,
    h: 5,
    hp: 1600,
    cost: { hollowglass: 22, gravesilver: 22, souls: 75 },
    buildTime: 60,
    // Beds for ninety and a settlement that hates you for it. This is the shape
    // of the whole Age in one building: the population to run a Hollow economy
    // is not a population that will thank you for the arrangement.
    popCap: 23,
    morale: -0.28,
    consumes: { bread: 0.12 },
    storage: 300,
    vis: { shape: 'hall', height: 1.9, detail: 3, banner: true, glow: 0.25, motif: 'cupola' },
  }),
  b({
    id: 'knell_tower',
    name: 'Knell Tower',
    flavour: 'One note, held. Everything inside the walls flinches; everything outside them stops.',
    category: 'defence',
    tier: 5,
    w: 2,
    h: 3,
    hp: 1400,
    cost: { gravesilver: 70, hollowglass: 50, souls: 150 },
    buildTime: 46,
    attack: { damage: 70, dmgType: DamageType.Arcane, range: 10.0, rate: 0.8, splash: 1.6, hitsFliers: true },
    morale: -0.06,
    vis: { shape: 'belfry', height: 3.0, detail: 3, glow: 0.9, motif: 'cupola' },
  }),

  // ── Housing & civic ──────────────────────────────────────────────────────
  b({
    id: 'house',
    name: 'Hovels',
    flavour: 'Four walls, a roof, and a spot by the door for the one who does not come back.',
    category: 'civic',
    w: 2,
    h: 2,
    hp: 220,
    cost: { timber: 8 },
    buildTime: 8,
    popCap: 3,
    vis: { shape: 'hut', height: 0.95, detail: 1, motif: 'chimney' },
  }),
  b({
    id: 'longhouse',
    name: 'Longhouse',
    flavour: 'Sleeps six, in a row, with the fire at the far end.',
    category: 'civic',
    tier: 2,
    w: 4,
    h: 2,
    hp: 420,
    cost: { timber: 20, planks: 6, stone: 5 },
    buildTime: 22,
    popCap: 6,
    morale: 0.03,
    consumes: { bread: 0.02 },
    vis: { shape: 'hut', height: 1.1, detail: 2, motif: 'chimneys' },
  }),
  b({
    id: 'chapel',
    name: 'Chapel of the Ninth Bell',
    flavour: 'They ring it for the dead. They have started ringing it in advance.',
    category: 'civic',
    w: 2,
    h: 3,
    hp: 380,
    cost: { stone: 60, timber: 30 },
    buildTime: 16,
    workers: 2,
    job: Job.Acolyte,
    produces: { souls: 0.05 },
    morale: 0.12,
    grants: ['sanctified_ground'],
    vis: { shape: 'shrine', height: 1.6, detail: 2, banner: true, motif: 'none' },
  }),
  b({
    id: 'tavern',
    name: 'The Drowned Sparrow',
    flavour: 'The ale is bad and the company is worse, and both are considered essential.',
    category: 'civic',
    w: 3,
    h: 2,
    hp: 300,
    cost: { timber: 55, planks: 15, grain: 40 },
    buildTime: 18,
    morale: 0.15,
    consumes: { bread: 0.06 },
    vis: { shape: 'hall', height: 1.05, detail: 3, glow: 0.4, motif: 'sign' },
  }),
  b({
    id: 'gallows',
    name: 'Gallows',
    flavour: 'Discipline is a comfort of sorts. The Sovereign is told it helps.',
    category: 'civic',
    w: 1,
    h: 1,
    hp: 160,
    cost: { timber: 20 },
    buildTime: 6,
    morale: 0.06,
    vis: { shape: 'gibbet', height: 1.5, detail: 0, motif: 'cages' },
  }),
  b({
    id: 'reliquary',
    name: 'Reliquary',
    flavour: 'Bones, teeth, a finger in a jar, and one heart that has not stopped.',
    category: 'civic',
    tier: 2,
    w: 2,
    h: 2,
    hp: 460,
    cost: { stone: 90, souls: 50 },
    buildTime: 20,
    morale: 0.08,
    grants: ['grave_call'],
    vis: { shape: 'shrine', height: 1.4, detail: 3, glow: 0.6, banner: true, motif: 'cupola' },
  }),
  /**
   * ── The Academy ─────────────────────────────────────────────────────────
   *
   * The building research is *bought from*, and the answer to the one thing the
   * owner pointed at that genuinely did not exist. Sixty tech nodes were bought
   * straight off a menu the moment their Age opened: the tree was a shopping
   * list, and a shopping list is not a thing you build toward.
   *
   * Three seats and three tiers, and the tier is the gate — see
   * `academyTier` and `doResearch` in `apply.ts`. Tier 1 opens the first two
   * Ages of every branch, tier 2 the next two, tier 3 the Hollow's. It is
   * cheap to raise and dear to upgrade, deliberately: nobody should be locked
   * out of Coursed Stone, and everybody should have to choose between the
   * Hollow's capstones and a wall.
   *
   * It is also where a race's *own* branch lives, and the only place it lives.
   * See `RACE_TECH` in `data/tech.ts` for why the shared sixty were extended
   * with a fourth column rather than forked per race.
   */
  b({
    id: 'academy',
    name: 'The Academy',
    flavour:
      'Four rooms, a locked one, and a standing argument about which of the four the locked one is.',
    category: 'civic',
    tier: 1,
    w: 3,
    h: 2,
    hp: 520,
    cost: { timber: 90, stone: 70, planks: 30 },
    buildTime: 30,
    morale: 0.04,
    storage: 60,
    maxTier: 3,
    vis: { shape: 'hall', height: 1.35, detail: 3, banner: true, glow: 0.15, motif: 'orrery' },
  }),

  /**
   * ── The Pale Litany's two ───────────────────────────────────────────────
   *
   * The building half of the owner's Mana sentence, and the reason the undead's
   * binding is a thing laid out on the ground rather than a number in a menu.
   * Both are `race: 'litany'`, so nobody else is offered them and nobody else
   * would have any use for them if they were.
   *
   * Two rather than one, and they are priced against each other rather than
   * stacked: the Ossuary is the wide cheap slab you put up early and put up
   * several of, and the Black Choir is dear, small, and worth three of them. A
   * Litany settlement that has spent the Bell's goods on Choirs fields an army
   * that a settlement of the same size cannot.
   */
  b({
    id: 'ossuary',
    name: 'Ossuary',
    flavour: 'Sorted by length. Nobody remembers agreeing to that and nobody has changed it.',
    category: 'civic',
    tier: 1,
    race: 'litany',
    researched: true,
    w: 3,
    h: 2,
    hp: 430,
    cost: { stone: 80, timber: 40 },
    buildTime: 22,
    mana: 55,
    storage: 40,
    vis: { shape: 'store', height: 1.1, detail: 2, glow: 0.25, motif: 'antlers' },
  }),
  b({
    id: 'black_choir',
    name: 'The Black Choir',
    flavour: 'Nine voices, none of them breathing. The ninth is the one you can hear from the gate.',
    category: 'civic',
    tier: 3,
    race: 'litany',
    researched: true,
    w: 2,
    h: 2,
    hp: 520,
    cost: { runestone: 90, soulwood: 60, souls: 180 },
    buildTime: 40,
    mana: 190,
    morale: -0.05,
    grants: ['toll_the_bell'],
    vis: { shape: 'belfry', height: 1.9, detail: 3, banner: true, glow: 0.7, motif: 'cages' },
  }),

  /**
   * ── The other ten peoples' pairs ────────────────────────────────────────
   *
   * Two apiece, and the rule is uniform with no exceptions: **every race
   * building is `researched: true` and is unlocked by exactly one node of that
   * race's own Academy column.** `tests/races.test.ts` asserts all three halves
   * of that — the building exists, it belongs to the race that lists it, and
   * precisely one node in that race's column opens it.
   *
   * The uniformity is the point rather than tidiness. It is what makes the
   * Academy the door to a *people's identity* and not merely to its statistics:
   * a Splitmoot player's first real decision is whether the Academy or the
   * Breeding Pit comes first, and there is no way to have the second without the
   * first. That is the answer to "a building that gates research of passives and
   * buildings" being a building rather than a menu.
   *
   * They are deliberately cheap in build time and dear in the race's own
   * currency where it has one. A people should be able to raise its identity in
   * the first Age and should have to keep paying for it afterwards.
   */

  // The Ashen Crown.
  b({
    id: 'levy_hall',
    name: 'Levy Hall',
    flavour: 'A desk, a queue, and a clerk who has stopped looking up.',
    category: 'military',
    tier: 1,
    race: 'ashen',
    researched: true,
    w: 3,
    h: 2,
    hp: 460,
    cost: { timber: 70, stone: 50 },
    buildTime: 20,
    popCap: 4,
    trains: ['militia', 'spear'],
    vis: { shape: 'barracks', height: 1.1, detail: 2, banner: true, motif: 'rack' },
  }),
  b({
    id: 'chancery',
    name: 'The Chancery',
    flavour: 'Where the debt lives. It is a larger room than anyone expected.',
    category: 'civic',
    tier: 2,
    race: 'ashen',
    researched: true,
    w: 2,
    h: 2,
    hp: 420,
    cost: { planks: 60, stone: 70, gold: 120 },
    buildTime: 26,
    workers: 3,
    job: Job.Smith,
    produces: { gold: 0.22 },
    consumes: { bread: 0.05 },
    storage: 120,
    morale: 0.04,
    vis: { shape: 'hall', height: 1.15, detail: 3, banner: true, motif: 'lantern' },
  }),

  // The Gilded Vigil.
  b({
    id: 'scriptorium',
    name: 'The Scriptorium',
    flavour: 'Four hundred names, and one hand permitted to write them.',
    category: 'civic',
    tier: 1,
    race: 'vigil',
    researched: true,
    w: 2,
    h: 2,
    hp: 400,
    cost: { timber: 60, stone: 60, souls: 30 },
    buildTime: 24,
    workers: 2,
    job: Job.Acolyte,
    popCap: 2,
    morale: 0.06,
    vis: { shape: 'shrine', height: 1.25, detail: 3, glow: 0.4, motif: 'lantern' },
  }),
  b({
    id: 'lamp_house',
    name: 'The Lamp House',
    flavour: 'Lit from underneath. Nobody will explain and nobody has stopped asking.',
    category: 'defence',
    tier: 2,
    race: 'vigil',
    researched: true,
    w: 2,
    h: 2,
    hp: 560,
    cost: { ingots: 60, stone: 90, souls: 60 },
    buildTime: 30,
    morale: 0.08,
    attack: { damage: 26, dmgType: DamageType.Fire, range: 9, rate: 0.8, splash: 1.4, hitsFliers: true },
    vis: { shape: 'tower', height: 2, detail: 3, glow: 0.9, banner: true, motif: 'lantern' },
  }),

  // Karrun-Dûr.
  b({
    id: 'deep_delving',
    name: 'Deep Delving',
    flavour: 'Down is cheaper than out, and always was.',
    category: 'civic',
    tier: 1,
    race: 'karrun',
    researched: true,
    w: 2,
    h: 2,
    hp: 700,
    cost: { stone: 28, timber: 10 },
    buildTime: 28,
    // The vertical hold: one footprint, five times a house, and it upgrades
    // further than anything else in the catalogue because Deepwrights do.
    popCap: 8,
    storage: 80,
    maxTier: 4,
    vis: { shape: 'mine', height: 0.9, detail: 2, motif: 'rack' },
  }),
  b({
    id: 'grudge_hall',
    name: 'The Grudge Hall',
    flavour: 'A room built to hold a list. The list has outgrown two of these.',
    category: 'civic',
    tier: 2,
    race: 'karrun',
    researched: true,
    w: 3,
    h: 2,
    hp: 820,
    cost: { runestone: 80, stone: 160, ingots: 50 },
    buildTime: 34,
    morale: 0.05,
    storage: 100,
    vis: { shape: 'hall', height: 1.3, detail: 3, banner: true, motif: 'rack' },
  }),

  // The Verdant Choir.
  b({
    id: 'heartgrove',
    name: 'The Heartgrove',
    flavour: 'The first ring. Everything since has been measured off it.',
    category: 'economy',
    tier: 1,
    race: 'verdant',
    researched: true,
    w: 3,
    h: 3,
    hp: 380,
    cost: { timber: 90 },
    buildTime: 30,
    workers: 4,
    job: Job.Farmer,
    produces: { grain: 0.2, timber: 0.12 },
    // No houses: the Choir's beds are groves, and a grove is worth four of
    // anybody's hovels because it is also the farm.
    popCap: 6,
    morale: 0.1,
    needsTerrain: [Terrain.Forest],
    vis: { shape: 'field', height: 1.6, detail: 3, glow: 0.2, motif: 'none' },
  }),
  b({
    id: 'thornwright_hall',
    name: 'Thornwright Hall',
    flavour: 'Grown in a hurry, and it shows in the thorns.',
    category: 'defence',
    tier: 3,
    race: 'verdant',
    researched: true,
    w: 2,
    h: 2,
    hp: 520,
    cost: { magewood: 120, soulwood: 60 },
    buildTime: 32,
    attack: { damage: 19, dmgType: DamageType.Poison, range: 8.5, rate: 1.1, splash: 0, hitsFliers: false },
    vis: { shape: 'tower', height: 1.7, detail: 3, glow: 0.35, motif: 'antlers' },
  }),

  // The Silver Reaches.
  b({
    id: 'amberworks',
    name: 'The Amberworks',
    flavour: 'Time, set aside, and labelled in a hand nobody now reads.',
    category: 'economy',
    tier: 1,
    race: 'silver',
    researched: true,
    w: 2,
    h: 2,
    hp: 340,
    cost: { stone: 70, timber: 60 },
    buildTime: 26,
    workers: 3,
    job: Job.Acolyte,
    produces: { essence: 0.06, gold: 0.08 },
    popCap: 3,
    storage: 60,
    vis: { shape: 'shrine', height: 1.3, detail: 3, glow: 0.55, motif: 'orrery' },
  }),
  b({
    id: 'sky_lantern_hall',
    name: 'Sky-Lantern Hall',
    flavour: 'Lit for ships that stopped coming. Still lit.',
    category: 'defence',
    tier: 3,
    race: 'silver',
    researched: true,
    w: 2,
    h: 2,
    hp: 480,
    cost: { quintessence: 90, voidstone: 80 },
    buildTime: 36,
    morale: 0.08,
    attack: { damage: 34, dmgType: DamageType.Arcane, range: 12, rate: 0.6, splash: 0.8, hitsFliers: true },
    vis: { shape: 'belfry', height: 2.1, detail: 3, glow: 1, banner: true, motif: 'lantern' },
  }),

  // The Drowned Court.
  b({
    id: 'thrall_pens',
    name: 'The Thrall Pens',
    flavour: 'Ventilated. This was considered, at the time, generous.',
    category: 'military',
    tier: 1,
    race: 'drowned',
    researched: true,
    w: 3,
    h: 2,
    hp: 420,
    cost: { timber: 80, stone: 40 },
    buildTime: 22,
    // The binding made a building: the Court's muster is what it has carried
    // off, and this is where what it carried off is kept.
    popCap: 6,
    morale: -0.06,
    trains: ['militia', 'spear'],
    vis: { shape: 'yard', height: 0.9, detail: 2, motif: 'cages' },
  }),
  b({
    id: 'ichor_still',
    name: 'The Ichor Still',
    flavour: 'Runs warm. It is meant to.',
    category: 'economy',
    tier: 2,
    race: 'drowned',
    researched: true,
    w: 2,
    h: 2,
    hp: 400,
    cost: { runestone: 60, ingots: 50 },
    buildTime: 26,
    workers: 3,
    job: Job.Smith,
    produces: { ichor: 0.14 },
    consumes: { souls: 0.05 },
    storage: 60,
    vis: { shape: 'forge', height: 1.15, detail: 3, glow: 0.6, motif: 'still' },
  }),

  // The Crimson Assize.
  b({
    id: 'ichor_cellar',
    name: 'The Ichor Cellar',
    flavour: 'Cool, dark, and inventoried weekly by somebody who enjoys it.',
    category: 'economy',
    tier: 1,
    race: 'crimson',
    researched: true,
    w: 2,
    h: 2,
    hp: 440,
    cost: { stone: 90, timber: 40 },
    buildTime: 24,
    workers: 3,
    job: Job.Smith,
    // The Thirst made a building. It takes grain the way a bakery does and
    // returns the thing the host actually drinks, which is why a Crimson town
    // is a farm town whose farms feed nobody who fights.
    produces: { ichor: 0.11 },
    consumes: { grain: 0.18 },
    storage: 90,
    morale: -0.04,
    vis: { shape: 'store', height: 0.95, detail: 2, glow: 0.3, motif: 'barrels' },
  }),
  b({
    id: 'bloodcourt',
    name: 'The Bloodcourt',
    flavour: 'It sits every night and it is always quorate.',
    category: 'civic',
    tier: 3,
    race: 'crimson',
    researched: true,
    w: 3,
    h: 3,
    hp: 720,
    cost: { alloy: 22, ichor: 35, souls: 30 },
    buildTime: 40,
    popCap: 5,
    morale: 0.1,
    trains: ['militia', 'cavalry'],
    vis: { shape: 'hall', height: 1.6, detail: 3, banner: true, glow: 0.4, motif: 'cages' },
  }),

  // The Splitmoot.
  b({
    id: 'breeding_pit',
    name: 'The Breeding Pit',
    flavour: 'Warm. Best not looked into, and there is no lid.',
    category: 'civic',
    tier: 1,
    race: 'splitmoot',
    researched: true,
    w: 3,
    h: 3,
    hp: 300,
    cost: { timber: 15, scrap: 8 },
    buildTime: 18,
    // No cap for the Splitmoot: the pit is what makes the press, and the press
    // is the problem rather than the supply. Housing here is *pressure*.
    popCap: 10,
    morale: -0.12,
    vis: { shape: 'pit', height: 0.6, detail: 1, motif: 'antlers' },
  }),
  b({
    id: 'shout_post',
    name: 'The Shout Post',
    flavour: 'Whoever is standing on it is in charge. Briefly.',
    category: 'military',
    tier: 2,
    race: 'splitmoot',
    researched: true,
    w: 2,
    h: 2,
    hp: 340,
    cost: { scrap: 90, timber: 70 },
    buildTime: 20,
    morale: 0.06,
    trains: ['militia', 'archer'],
    vis: { shape: 'tower', height: 1.5, detail: 1, banner: true, motif: 'sign' },
  }),

  // The Cog-Warrens.
  // ── The east ─────────────────────────────────────────────────────────────
  //
  // Two apiece for the three peoples of the new continent. The rule every one
  // of them is held to is the roster's: a race building must be `researched`,
  // must be claimed by its own race in `races.ts`, and must be opened by
  // exactly one node of that race's own Academy column — `tests/races.test.ts`
  // probes the closures to check the last of those, because the catalogue and
  // the unlocks have been authored an hour apart before and two of them ended
  // up as content nobody could ever build.

  // The Long Noon. The terrace is load-bearing in a way no other race building
  // is: `recomputePopCap` counts an `economy` building with a `popCap` as a
  // grove, and the `groves` binding forbids the house — so a Long Noon with no
  // terrace has the Hearth and the barrack-rooms and nothing else. It is `tier`
  // 1 and cheap for exactly that reason.
  b({
    id: 'sun_terrace',
    name: 'The Sun Terrace',
    flavour: 'Stone laid flat, facing the right way, and warm until well after dark.',
    category: 'economy',
    tier: 1,
    race: 'longnoon',
    researched: true,
    w: 3,
    h: 3,
    hp: 420,
    cost: { stone: 100 },
    buildTime: 30,
    workers: 4,
    job: Job.Farmer,
    produces: { grain: 0.18 },
    // No houses: the Long Noon sleep on the terrace, and the terrace is also
    // where everything they grow is dried. Worth what a grove is worth for the
    // same reason — it is the farm as well as the bed.
    popCap: 6,
    morale: 0.1,
    vis: { shape: 'field', height: 1.2, detail: 3, glow: 0.14, motif: 'colonnade' },
  }),
  b({
    id: 'augur_stone',
    name: 'The Augur Stone',
    flavour: 'It has been read for eight hundred years and has said the same thing twice.',
    category: 'defence',
    tier: 2,
    race: 'longnoon',
    researched: true,
    w: 2,
    h: 2,
    hp: 620,
    cost: { stone: 140, runestone: 70 },
    buildTime: 34,
    attack: { damage: 26, dmgType: DamageType.Arcane, range: 9.5, rate: 0.7, splash: 1.2, hitsFliers: true },
    vis: { shape: 'tower', height: 1.9, detail: 3, glow: 0.4, motif: 'orrery' },
  }),

  // The Undertow.
  b({
    id: 'sump_market',
    name: 'The Sump Market',
    flavour: 'Open every hour there has ever been. Half of it is under water and that is a schedule, not a problem.',
    category: 'economy',
    tier: 1,
    race: 'undertow',
    researched: true,
    w: 3,
    h: 2,
    hp: 300,
    cost: { timber: 90, scrap: 40 },
    buildTime: 26,
    workers: 4,
    job: Job.Carter,
    produces: { gold: 0.22, scrap: 0.06 },
    storage: 180,
    morale: 0.06,
    vis: { shape: 'market', height: 0.9, detail: 3, banner: true, motif: 'scales' },
  }),
  b({
    id: 'weir_gate',
    name: 'The Weir Gate',
    flavour: 'It does not stop anybody. It decides how long they are in the water on the way.',
    category: 'defence',
    tier: 2,
    race: 'undertow',
    researched: true,
    w: 2,
    h: 2,
    hp: 540,
    cost: { timber: 120, stone: 80, scrap: 60 },
    buildTime: 30,
    attack: { damage: 14, dmgType: DamageType.Poison, range: 8, rate: 1.6, splash: 1.6, hitsFliers: false },
    vis: { shape: 'tower', height: 1.2, detail: 2, motif: 'none' },
  }),

  // The Thirstless.
  b({
    id: 'standing_kin',
    name: 'The Standing Kin',
    flavour: 'One of them stopped here. Nobody has established whether that was a decision.',
    category: 'defence',
    tier: 1,
    race: 'thirstless',
    researched: true,
    w: 2,
    h: 2,
    hp: 900,
    cost: { stone: 160 },
    buildTime: 36,
    morale: 0.12,
    attack: { damage: 44, dmgType: DamageType.Blunt, range: 6.5, rate: 0.4, splash: 1.4, hitsFliers: false },
    vis: { shape: 'shrine', height: 2.2, detail: 2, motif: 'colonnade' },
  }),
  b({
    id: 'salt_kiln',
    name: 'The Salt Kiln',
    flavour: 'Sand in, glass out, and a great deal of patience in the middle.',
    category: 'economy',
    tier: 2,
    race: 'thirstless',
    researched: true,
    w: 3,
    h: 2,
    hp: 460,
    cost: { stone: 120, iron: 50 },
    buildTime: 32,
    workers: 3,
    job: Job.Smith,
    produces: { ingots: 0.09 },
    consumes: { stone: 0.16 },
    storage: 120,
    vis: { shape: 'oven', height: 1.4, detail: 3, glow: 0.3, motif: 'chimney' },
  }),

  b({
    id: 'salvage_yard',
    name: 'The Salvage Yard',
    flavour: 'Sorted into piles by a system nobody has written down and everybody obeys.',
    category: 'economy',
    tier: 1,
    race: 'cogwarren',
    researched: true,
    w: 3,
    h: 3,
    hp: 360,
    cost: { timber: 50, iron: 40 },
    buildTime: 20,
    workers: 4,
    job: Job.Smith,
    // The salvage loop, at its most literal: iron in, scrap out, and the
    // Cog-Warrens' whole column is priced in what comes out.
    produces: { scrap: 0.3 },
    consumes: { iron: 0.06 },
    storage: 110,
    vis: { shape: 'yard', height: 0.8, detail: 2, motif: 'cog' },
  }),
  b({
    id: 'cog_works',
    name: 'The Cog Works',
    flavour: 'Improved twice since Tuesday. Nobody is sure which two.',
    category: 'military',
    tier: 3,
    race: 'cogwarren',
    researched: true,
    w: 3,
    h: 3,
    hp: 640,
    cost: { alloy: 80, scrap: 220, ingots: 90 },
    buildTime: 38,
    // The crews binding, charged the way the Hollow charges: heads off its own
    // shift, permanently, from the third Age rather than the fifth.
    toll: 150,
    workers: 5,
    job: Job.Smith,
    produces: { scrap: 0.5, ingots: 0.1 },
    consumes: { iron: 0.14 },
    trains: ['siege'],
    vis: { shape: 'forge', height: 1.5, detail: 3, glow: 0.7, banner: true, motif: 'cog' },
  }),

  // The Moonbound.
  b({
    id: 'howl_stone',
    name: 'The Howl Stone',
    flavour: 'Older than the pack. Older than the language the pack howls in.',
    category: 'civic',
    tier: 1,
    race: 'moonbound',
    researched: true,
    w: 2,
    h: 2,
    hp: 900,
    cost: { stone: 25 },
    buildTime: 24,
    popCap: 4,
    morale: 0.12,
    grants: ['iron_resolve'],
    vis: { shape: 'shrine', height: 1.4, detail: 1, glow: 0.3, motif: 'antlers' },
  }),
  b({
    id: 'skinning_camp',
    name: 'The Skinning Camp',
    flavour: 'Everything is used. That is not a virtue, it is arithmetic.',
    category: 'economy',
    tier: 2,
    race: 'moonbound',
    researched: true,
    w: 3,
    h: 2,
    hp: 300,
    cost: { timber: 70, scrap: 50 },
    buildTime: 18,
    workers: 4,
    job: Job.Farmer,
    produces: { grain: 0.22, scrap: 0.12 },
    // No storage at all, on a people who cannot store. Deliberately zero rather
    // than small: the No Hoard passive would scale a number, and a zero says it.
    storage: 0,
    vis: { shape: 'camp', height: 0.7, detail: 2, motif: 'antlers' },
  }),

  b({
    id: 'war_room',
    name: 'War Room',
    flavour: 'A table, a map, and eleven markers for companies that number nine.',
    category: 'civic',
    tier: 2,
    w: 3,
    h: 2,
    hp: 480,
    cost: { stone: 70, timber: 50, planks: 30, iron: 20 },
    buildTime: 26,
    morale: 0.05,
    grants: ['rally_banner'],
    vis: { shape: 'hall', height: 1.2, detail: 3, banner: true, motif: 'antlers' },
  }),
  b({
    id: 'infirmary',
    name: 'Infirmary',
    flavour: 'Where the arithmetic of who can be saved is done out loud.',
    category: 'civic',
    tier: 2,
    w: 3,
    h: 2,
    hp: 320,
    cost: { timber: 60, grain: 30 },
    buildTime: 15,
    workers: 2,
    job: Job.Acolyte,
    morale: 0.07,
    vis: { shape: 'hall', height: 1.0, detail: 2, motif: 'chimneys' },
  }),
  b({
    id: 'bell_tower',
    tech: 'sac_bellwork',
    name: 'Bell Tower',
    flavour: 'Nine bells were cast. Eight are accounted for.',
    category: 'civic',
    tier: 3,
    w: 2,
    h: 2,
    hp: 520,
    cost: { stone: 110, iron: 50 },
    buildTime: 24,
    morale: 0.1,
    grants: ['toll_the_bell'],
    vis: { shape: 'belfry', height: 2.3, detail: 3, banner: true, motif: 'none' },
  }),

  // ── Military ─────────────────────────────────────────────────────────────
  b({
    id: 'barracks',
    name: 'Barracks',
    flavour: 'Bunks for a hundred, names on eighty, and the sergeant will not say which eighty.',
    category: 'military',
    w: 3,
    h: 3,
    hp: 620,
    cost: { timber: 60, stone: 30 },
    buildTime: 18,
    // Barracks bunk their own: military buildings carry the soldiers they drill,
    // so an army does not compete with the town for beds.
    popCap: 5,
    trains: ['militia', 'spear', 'heavy'],
    vis: { shape: 'barracks', height: 1.15, detail: 2, banner: true, motif: 'none' },
  }),
  b({
    id: 'archery_range',
    name: 'Archery Range',
    flavour: 'The butts are made of the old chapel door. It absorbs well.',
    category: 'military',
    w: 4,
    h: 2,
    hp: 420,
    cost: { timber: 70 },
    buildTime: 16,
    popCap: 4,
    trains: ['archer', 'crossbow'],
    vis: { shape: 'range', height: 0.8, detail: 2, motif: 'rack' },
  }),
  b({
    id: 'stable',
    name: 'Stables',
    flavour: 'Twelve stalls. Nine horses. Three doors nobody opens.',
    category: 'military',
    tier: 2,
    w: 3,
    h: 3,
    hp: 480,
    cost: { timber: 80, grain: 60 },
    buildTime: 20,
    popCap: 3,
    trains: ['cavalry'],
    vis: { shape: 'stable', height: 1.0, detail: 2, motif: 'rack' },
  }),
  b({
    id: 'drill_yard',
    name: 'Drill Yard',
    flavour: 'Mud in the shape of a square. Reliably the muddiest square in the realm.',
    category: 'military',
    tier: 2,
    w: 4,
    h: 4,
    hp: 300,
    cost: { timber: 50, stone: 20 },
    buildTime: 14,
    vis: { shape: 'yard', height: 0.35, detail: 1, motif: 'none' },
  }),
  b({
    id: 'armoury',
    name: 'Armoury',
    flavour: 'Sorted by size. Then by whose it was.',
    category: 'military',
    tier: 2,
    w: 2,
    h: 3,
    hp: 500,
    cost: { stone: 60, iron: 60 },
    buildTime: 19,
    vis: { shape: 'store', height: 1.0, detail: 3, motif: 'rack' },
  }),
  b({
    id: 'siege_yard',
    name: 'Siege Yard',
    flavour: 'Everything here is built to be used once, loudly.',
    category: 'military',
    tier: 3,
    w: 4,
    h: 3,
    hp: 560,
    cost: { timber: 110, planks: 60, iron: 70, stone: 40 },
    buildTime: 34,
    trains: ['siege'],
    vis: { shape: 'yard', height: 1.0, detail: 3, motif: 'crane' },
  }),
  b({
    id: 'mage_tower',
    name: 'Mage Tower',
    flavour: 'The occupant is asked, weekly, to stop. The occupant is weekly indispensable.',
    category: 'military',
    tier: 3,
    w: 2,
    h: 2,
    hp: 460,
    cost: { stone: 100, souls: 60, iron: 30 },
    buildTime: 26,
    trains: ['caster'],
    // Chain Lightning was here; see `RETIRED_POWERS`. A tower full of
    // casters granting the power that stops a charge dead reads better than
    // one granting the power that deleted it.
    grants: ['time_in_amber'],
    vis: { shape: 'tower', height: 2.8, detail: 3, glow: 0.9, motif: 'orrery' },
  }),
  b({
    id: 'magewright_hall',
    name: 'Magewright’s Hall',
    flavour:
      'Armour fitted twice: once to the man, once to whatever it is the plates have been listening to.',
    category: 'military',
    tier: 2,
    w: 3,
    h: 3,
    hp: 560,
    // The first thing in the game that cannot be bought with Age-1 goods at
    // all. If the chain is not running, this building does not exist for you.
    cost: { magewood: 70, ingots: 50, planks: 40 },
    buildTime: 30,
    popCap: 5,
    trains: ['militia', 'spear', 'heavy', 'caster'],
    morale: 0.04,
    vis: { shape: 'hall', height: 1.3, detail: 3, banner: true, glow: 0.5, motif: 'still' },
  }),
  b({
    id: 'kennel',
    name: 'Kennels',
    flavour: 'They are fed first. There is a reason for this and nobody enjoys it.',
    category: 'military',
    tier: 2,
    w: 2,
    h: 2,
    hp: 300,
    cost: { timber: 45, grain: 40 },
    buildTime: 13,
    vis: { shape: 'stable', height: 0.7, detail: 2, motif: 'none' },
  }),

  // ── Defence ──────────────────────────────────────────────────────────────
  b({
    id: 'arrow_tower',
    name: 'Arrow Tower',
    flavour: 'Two archers, one brazier, and a view they would rather not have.',
    category: 'defence',
    w: 2,
    h: 2,
    hp: 700,
    cost: { timber: 50, stone: 40 },
    buildTime: 15,
    attack: { damage: 13, dmgType: DamageType.Arrow, range: 8.0, rate: 1.1, splash: 0, hitsFliers: true },
    vis: { shape: 'tower', height: 2.3, detail: 2, banner: true, motif: 'barrels' },
  }),
  b({
    id: 'ballista_tower',
    name: 'Ballista Tower',
    flavour: 'Bolts the length of a man. Used, once, on a man.',
    category: 'defence',
    tier: 2,
    w: 2,
    h: 2,
    hp: 820,
    cost: { timber: 70, planks: 40, stone: 70, iron: 40 },
    buildTime: 28,
    attack: { damage: 58, dmgType: DamageType.Siege, range: 10.0, rate: 0.28, splash: 0.7, hitsFliers: true },
    vis: { shape: 'tower', height: 2.4, detail: 3, motif: 'crane' },
  }),
  b({
    id: 'brazier',
    name: 'Watch Brazier',
    flavour: 'Light enough to see by. Light enough to be seen by.',
    category: 'defence',
    w: 1,
    h: 1,
    hp: 180,
    cost: { timber: 15, iron: 5 },
    buildTime: 5,
    morale: 0.02,
    vis: { shape: 'brazier', height: 1.2, detail: 0, glow: 1, motif: 'none' },
  }),
  b({
    id: 'oil_vat',
    name: 'Oil Vat',
    flavour: 'Kept hot. Kept, mostly, secret from the men who stand under it.',
    category: 'defence',
    tier: 2,
    w: 1,
    h: 1,
    hp: 240,
    cost: { iron: 30, timber: 20 },
    buildTime: 9,
    attack: { damage: 22, dmgType: DamageType.Fire, range: 2.6, rate: 0.6, splash: 1.4, hitsFliers: false },
    vis: { shape: 'pit', height: 0.6, detail: 1, glow: 0.6, motif: 'barrels' },
  }),
  b({
    id: 'trench',
    name: 'Trench Line',
    flavour: 'Dug by people who understood exactly what it was for.',
    category: 'defence',
    w: 1,
    h: 1,
    hp: 300,
    cost: { timber: 8 },
    buildTime: 4,
    vis: { shape: 'pit', height: 0.2, detail: 0, motif: 'rack' },
  }),
  b({
    id: 'caltrop_field',
    name: 'Caltrop Field',
    flavour: 'Cheap, cruel, and entirely beneath the dignity of the men who insisted on it.',
    category: 'defence',
    w: 2,
    h: 2,
    hp: 120,
    cost: { iron: 20 },
    buildTime: 5,
    vis: { shape: 'field', height: 0.15, detail: 0, motif: 'none' },
  }),
  b({
    id: 'watchtower',
    name: 'Watchtower',
    flavour: 'Sees far. Reports later than anyone would like.',
    category: 'defence',
    w: 2,
    h: 2,
    hp: 380,
    cost: { timber: 35 },
    buildTime: 8,
    attack: { damage: 8, dmgType: DamageType.Arrow, range: 7.0, rate: 0.8, splash: 0, hitsFliers: true },
    vis: { shape: 'tower', height: 2.0, detail: 1, motif: 'none' },
  }),
  b({
    id: 'bulwark',
    name: 'Bulwark',
    flavour: 'A wall that has been given a promotion and a small garrison.',
    category: 'defence',
    tier: 3,
    w: 2,
    h: 2,
    hp: 1400,
    cost: { stone: 140, iron: 40 },
    buildTime: 26,
    attack: { damage: 20, dmgType: DamageType.Pierce, range: 6.5, rate: 1.0, splash: 0, hitsFliers: true },
    vis: { shape: 'tower', height: 1.5, detail: 2, banner: true, motif: 'rack' },
  }),
  b({
    id: 'wardstone_tower',
    name: 'Wardstone Tower',
    flavour: 'It does not shoot so much as disagree, at range, with something being alive.',
    category: 'defence',
    tier: 2,
    w: 2,
    h: 2,
    hp: 760,
    cost: { runestone: 60, ingots: 40, stone: 60 },
    buildTime: 30,
    // Arcane cuts evenly through mail and plate and cares nothing for shields,
    // which is precisely the wave an Age-2 settlement starts meeting: the arrow
    // tower's answer stops working somewhere around the first armoured line.
    attack: { damage: 31, dmgType: DamageType.Arcane, range: 8.5, rate: 0.7, splash: 0.6, hitsFliers: true },
    vis: { shape: 'tower', height: 2.5, detail: 3, glow: 0.9, motif: 'cupola' },
  }),
  b({
    id: 'pyre',
    name: 'Corpse Pyre',
    flavour: 'Burns the dead so the Rotmoot cannot borrow them.',
    category: 'defence',
    tier: 2,
    w: 2,
    h: 2,
    hp: 260,
    cost: { timber: 40, iron: 10 },
    buildTime: 10,
    produces: { souls: 0.04 },
    morale: -0.02,
    vis: { shape: 'pit', height: 0.5, detail: 1, glow: 1, motif: 'chimney' },
  }),
  // Workshop expansion. Append: existing saves store building type indices.
  b({
    id: 'charcoal_kiln', name: 'Charcoal Clamp',
    flavour: 'Wood stacked under earth, watched through the night. Smoke is lost fuel.',
    category: 'economy', tier: 1, w: 3, h: 3, hp: 360,
    cost: { timber: 55, stone: 30 }, buildTime: 18, workers: 2, job: Job.Smith,
    consumes: { timber: 0.2 }, produces: { charcoal: 0.16 },
    vis: { shape: 'oven', height: 0.9, detail: 2, glow: 0.35, motif: 'chimney' },
  }),
  b({
    id: 'steelworks', name: 'Steelworks',
    flavour: 'The second heat takes longer. The smith knows when to stop by the sparks.',
    category: 'economy', tier: 2, w: 3, h: 3, hp: 540,
    cost: { planks: 75, stone: 95, iron: 40 }, buildTime: 28, workers: 4, job: Job.Smith,
    consumes: { ingots: 0.18, charcoal: 0.08 }, produces: { steel: 0.09 },
    vis: { shape: 'forge', height: 1.3, detail: 3, glow: 0.6, motif: 'chimneys' },
  }),
  b({
    id: 'toolsmith', name: 'Toolsmith',
    flavour: 'A good chisel makes a hundred things before it becomes a bad chisel.',
    category: 'economy', tier: 2, w: 3, h: 2, hp: 450,
    cost: { planks: 55, iron: 45, stone: 35 }, buildTime: 24, workers: 3, job: Job.Smith,
    consumes: { steel: 0.08, planks: 0.04 }, produces: { tools: 0.08 },
    vis: { shape: 'yard', height: 0.95, detail: 2, motif: 'rack' },
  }),
  b({
    id: 'frame_workshop', name: 'Framewright’s Workshop',
    flavour: 'Jigs, braces and numbered beams. The frame must agree before the engine can.',
    category: 'economy', tier: 2, w: 4, h: 3, hp: 490,
    cost: { planks: 80, stone: 35, iron: 35 }, buildTime: 28, workers: 3, job: Job.Smith,
    consumes: { planks: 0.2, timber: 0.16, tools: 0.02 }, produces: { frame: 0.04 },
    vis: { shape: 'sawmill', height: 1.1, detail: 2, motif: 'crane' },
  }),
  b({
    id: 'fitting_foundry', name: 'Fittings Foundry',
    flavour: 'Pins, bands and gears, cast in batches and rejected one by one.',
    category: 'economy', tier: 2, w: 3, h: 3, hp: 520,
    cost: { stone: 100, planks: 65, iron: 45 }, buildTime: 30, workers: 3, job: Job.Smith,
    consumes: { steel: 0.08, iron: 0.2, charcoal: 0.04 }, produces: { fitting: 0.04 },
    vis: { shape: 'forge', height: 1.1, detail: 2, glow: 0.5, motif: 'scales' },
  }),
  b({
    id: 'armament_workshop', name: 'Armament Workshop',
    flavour: 'Not a shelf of swords: a shift of people measuring the men who will carry them.',
    category: 'economy', tier: 3, w: 4, h: 3, hp: 660,
    cost: { magewood: 55, runestone: 50, ingots: 75, tools: 20 }, buildTime: 34, workers: 4, job: Job.Smith,
    consumes: { steel: 0.12, tools: 0.03, planks: 0.06 }, produces: { armaments: 0.06 },
    vis: { shape: 'barracks', height: 1.15, detail: 3, banner: true, motif: 'rack' },
  }),
  b({
    id: 'runic_joinery', name: 'Runic Joinery',
    flavour: 'The frame is fitted twice: once to the carriage, once to what the carriage will hold.',
    category: 'economy', tier: 3, w: 4, h: 3, hp: 590,
    cost: { magewood: 70, runestone: 35, tools: 20, frame: 12 }, buildTime: 36, workers: 4, job: Job.Smith,
    consumes: { frame: 0.03, magewood: 0.12, soulwood: 0.06 }, produces: { sigil_frame: 0.03 },
    vis: { shape: 'sawmill', height: 1.3, detail: 3, glow: 0.45, motif: 'orrery' },
  }),
  b({
    id: 'lens_workshop', name: 'Lens and Ring Workshop',
    flavour: 'The lens shows nothing. The ring decides where that nothing goes.',
    category: 'economy', tier: 3, w: 3, h: 3, hp: 580,
    cost: { runestone: 70, ingots: 40, tools: 20, fitting: 12 }, buildTime: 36, workers: 4, job: Job.Smith,
    consumes: { fitting: 0.03, alloy: 0.09, runestone: 0.09 }, produces: { focusing_ring: 0.03 },
    vis: { shape: 'hall', height: 1.2, detail: 3, glow: 0.55, motif: 'orrery' },
  }),
  b({
    id: 'artificers_exchange', name: 'Artificers’ Exchange',
    flavour: 'The surplus leaves in guarded wagons. Their return is counted in coin.',
    category: 'economy', tier: 3, w: 4, h: 3, hp: 620,
    cost: { magewood: 90, runestone: 50, tools: 35, gold: 180 }, buildTime: 36, workers: 3, job: Job.Smith,
    consumes: { tools: 0.015, armaments: 0.01 }, produces: { gold: 0.55 }, storage: 250,
    vis: { shape: 'market', height: 1.15, detail: 3, motif: 'scales' },
  }),
  b({
    id: 'core_foundry', name: 'Core Foundry',
    flavour: 'The glass is fitted cold, the iron warm. The fit is what keeps it quiet.',
    category: 'economy', tier: 4, w: 4, h: 4, hp: 780,
    cost: { soulwood: 60, voidstone: 90, alloy: 60, focusing_ring: 12 }, buildTime: 44, workers: 5, job: Job.Smith,
    consumes: { fitting: 0.03, heartiron: 0.09, ashglass: 0.06 }, produces: { gilded_core: 0.03 },
    vis: { shape: 'forge', height: 1.65, detail: 3, glow: 0.9, motif: 'cog' },
  }),
  b({
    id: 'grand_arsenal', name: 'Grand Arsenal',
    flavour: 'A reserve of engines and spare parts, behind doors thick enough to discourage an argument.',
    category: 'military', tier: 4, w: 5, h: 4, hp: 1600,
    cost: { stone: 180, heartiron: 65, tools: 50, armaments: 45, gilded_core: 12 }, buildTime: 48,
    storage: 1200, popCap: 12, trains: ['siege', 'crossbow'],
    vis: { shape: 'barracks', height: 1.6, detail: 3, banner: true, motif: 'crane' },
  }),
  b({
    id: 'heart_assembly', name: 'Hollow Heart Assembly',
    flavour: 'The parts arrive in padded boxes. The finished heart leaves in a cage.',
    category: 'economy', tier: 5, w: 4, h: 4, hp: 920,
    cost: { ashglass: 90, heartiron: 80, gilded_core: 18, souls: 160 }, buildTime: 52, workers: 6, job: Job.Smith,
    consumes: { focusing_ring: 0.02, hollowglass: 0.06, gravesilver: 0.06, souls: 0.5 }, produces: { hollow_heart: 0.02 },
    vis: { shape: 'forge', height: 1.8, detail: 3, glow: 0.8, motif: 'cages' },
  }),
  b({
    id: 'hollow_sanctuary', name: 'Hollow Sanctuary',
    flavour: 'Its heart holds the light steady. The people beneath it can finally sleep.',
    category: 'civic', tier: 5, w: 5, h: 5, hp: 1500,
    cost: { gravesilver: 80, ashglass: 90, gilded_core: 15, hollow_heart: 12 }, buildTime: 56,
    popCap: 50, mana: 150, morale: 0.1, storage: 500, consumes: { souls: 0.03 },
    vis: { shape: 'shrine', height: 2.1, detail: 3, glow: 0.6, motif: 'colonnade' },
  }),
];

const BUILDING_BY_ID = new Map<string, number>();
BUILDING_TYPES.forEach((t, i) => BUILDING_BY_ID.set(t.id, i));

export function buildingTypeIndex(id: string): number {
  const i = BUILDING_BY_ID.get(id);
  if (i === undefined) throw new Error(`Unknown building type: ${id}`);
  return i;
}

export function buildingType(index: number): BuildingType {
  return BUILDING_TYPES[index] ?? BUILDING_TYPES[0];
}

export function buildingTypeById(id: string): BuildingType {
  return BUILDING_TYPES[buildingTypeIndex(id)];
}

export const HEARTH_TYPE = buildingTypeIndex('hearth');

/**
 * Wall tiers — the defensive layers.
 *
 * A wall is no longer one thing you can or cannot afford. It starts as a
 * timber palisade you can throw up in the first build phase out of what the
 * woodcutters already brought in, and it is *upgraded in place* as the realm
 * gets richer. That matters for two reasons:
 *
 *   The first ring is affordable immediately, so a new player gets to find out
 *   what a wall is for before the economy could possibly pay for masonry. The
 *   balance sweep found runs ending with roughly one wall tile standing because
 *   stone was the binding constraint on the only tier that existed.
 *
 *   And because upgrading is per-tile, the interesting decision is *which*
 *   stretch. You cannot afford bastions all the way round. You can afford them
 *   across the northern approach, and a palisade everywhere else, and then the
 *   director notices where you are strong and comes the other way.
 */
export interface WallTierDef {
  name: string;
  flavour: string;
  /** Cost to raise a tile *to* this tier from the one below. */
  cost: Partial<ResourceBundle>;
  hp: number;
}

export const WALL_TIERS: readonly WallTierDef[] = [
  {
    name: 'Palisade',
    flavour: 'Sharpened logs in a ditch. It will not stop them. It will slow them where you want them slowed.',
    cost: { timber: 12, stone: 2 },
    hp: 300,
  },
  {
    name: 'Stone Wall',
    flavour: 'Stone stacked by people who will be standing behind it.',
    cost: { stone: 16, timber: 4 },
    hp: 760,
  },
  {
    name: 'Bastion',
    flavour: 'Thick enough that the sappers have to mean it, and angled so the archers can see them try.',
    cost: { stone: 26, iron: 10, planks: 8 },
    hp: 1650,
  },
];

export const MAX_WALL_TIER = WALL_TIERS.length;

export function wallTierDef(tier: number): WallTierDef {
  return WALL_TIERS[Math.max(0, Math.min(WALL_TIERS.length - 1, tier - 1))];
}

/** Wall and gate live on the tile layer; these are their economics. */
export const WALL_DEF = {
  name: 'Wall',
  flavour: 'Stone stacked by people who will be standing behind it.',
  cost: WALL_TIERS[0].cost,
  hp: WALL_TIERS[0].hp,
  buildTime: 2.2,
  armour: ArmourType.Structure,
};

/**
 * The ditch. Not a wall — a wall you have to climb *out of* while somebody
 * shoots at you. It costs almost nothing but labour, cannot be built on, and
 * makes the ground in front of your line the slowest ground on the map. The
 * cheapest defensive layer in the game and the one that most changes where the
 * horde chooses to walk, because the flow field genuinely routes around it.
 */
export const MOAT_DEF = {
  name: 'Ditch',
  flavour: 'A hole, dug with intent. The intent is the expensive part.',
  cost: { timber: 2 } as Partial<ResourceBundle>,
};

/**
 * A span of bridge, per tile.
 *
 * Dearer than a ditch by a lot, because it is the answer to the one piece of
 * terrain that costs an army real time — and because a bridge the player lays
 * casually is a bridge the horde walks over. Timber and iron rather than stone:
 * this is carpentry over water, not masonry.
 */
export const BRIDGE_DEF = {
  name: 'Bridge',
  flavour: 'Planks, pegs, and a great deal of faith in both.',
  cost: { timber: 14, iron: 4 } as Partial<ResourceBundle>,
};

export const GATE_DEF = {
  name: 'Gate',
  flavour: 'The only part of a wall that is designed to fail, on purpose, later.',
  cost: { timber: 30, iron: 20 } as Partial<ResourceBundle>,
  hp: 760,
  buildTime: 5,
  armour: ArmourType.Structure,
};

/**
 * The tower. A place, not a stat.
 *
 * It is priced *per tile* and it occupies four of them, so a tower costs about
 * as much as fourteen tiles of stone wall and is worth it for one reason: it is
 * the only part of the curtain that will hold a company. Eight men on a hard
 * point, shooting four tiles further than they could from the ground, and an
 * attacker who wants the line has to take it.
 *
 * The health is per tile as well, and it is roughly a bastion's — which makes a
 * whole tower four bastions' worth of chewing, and makes knocking one down a
 * decision the horde has to commit to rather than something that happens on the
 * way past. It is not upgradeable: a tower is already the top of the ladder, and
 * a tier system on top of a tier system is two dials for one decision.
 */
export const TOWER_DEF = {
  name: 'Tower',
  flavour: 'Somewhere to put the archers, and something for the ladders to be about.',
  /** Per tile of the 2×2 footprint. */
  cost: { stone: 30, timber: 14, iron: 8 } as Partial<ResourceBundle>,
  hp: 1500,
  buildTime: 8,
  armour: ArmourType.Structure,
};

/** Buildings the player can place from the very first build phase. */
export const STARTING_BUILDINGS = [
  'house',
  'woodcutter',
  'sawmill',
  'quarry',
  'iron_mine',
  'farm',
  'bakery',
  'granary',
  'storehouse',
  'market',
  'barracks',
  'archery_range',
];

/** The production chain, for the codex and the build-menu tooltips. */
export const PRODUCTION_CHAIN: Array<{ from: string; via: string; to: string }> = [
  { from: 'Timber', via: 'Sawmill', to: 'Planks' },
  { from: 'Grain', via: 'Bakery', to: 'Bread' },
  { from: 'Timber + Stone', via: 'Smelter', to: 'Iron' },
  { from: 'Corpses', via: 'Soul Furnace', to: 'Souls' },
  // The Kindling. Read the left-hand column: every one of them still starts at
  // an Age-1 camp, which is the whole of the accumulate model in four lines.
  { from: 'A leyline', via: 'Wellspring', to: 'Essence' },
  { from: 'Timber + Essence', via: 'Arcane Sawmill', to: 'Magewood' },
  { from: 'Stone + Essence', via: 'Runeworks', to: 'Runestone' },
  { from: 'Iron + Charcoal', via: 'Smeltery', to: 'Ingots' },
  // The Bell. Same reading: the left-hand column is the Age above's whole output.
  { from: 'Magewood + Souls', via: 'Grafting House', to: 'Soulwood' },
  { from: 'Runestone + Essence', via: 'Void Kiln', to: 'Voidstone' },
  { from: 'Ingots + Runestone', via: 'Rune Forge', to: 'Alloy' },
  { from: 'Essence + Souls', via: 'Distillery', to: 'Quintessence' },
  // The Gilded.
  { from: 'Voidstone + Quintessence', via: 'Ashworks', to: 'Ashglass' },
  { from: 'Alloy + Quintessence', via: 'The Great Forge', to: 'Heartiron' },
  // The Hollow, and the third column of each is not the whole price. See `toll`.
  { from: 'Ashglass + Souls + a life', via: 'The Hollowing', to: 'Hollowglass' },
  { from: 'Heartiron + Souls + a life', via: 'Mourning Forge', to: 'Gravesilver' },
  { from: 'Timber', via: 'Charcoal Clamp', to: 'Charcoal' },
  { from: 'Ingots + Charcoal', via: 'Steelworks', to: 'Steel' },
  { from: 'Steel + Planks', via: 'Toolsmith', to: 'Tools' },
  { from: 'Planks + Timber + Tools', via: 'Framewright’s Workshop', to: 'Frames' },
  { from: 'Steel + Iron + Charcoal', via: 'Fittings Foundry', to: 'Fittings' },
  { from: 'Steel + Tools + Planks', via: 'Armament Workshop', to: 'Armaments' },
  { from: 'Frames + Magewood + Soulwood', via: 'Runic Joinery', to: 'Sigil Frames' },
  { from: 'Fittings + Alloy + Runestone', via: 'Lens and Ring Workshop', to: 'Focusing Rings' },
  { from: 'Fittings + Heartiron + Ashglass', via: 'Core Foundry', to: 'Gilded Cores' },
  { from: 'Focusing Rings + Hollowglass + Gravesilver + Souls', via: 'Hollow Heart Assembly', to: 'Hollow Hearts' },
  { from: 'Tools + Armaments', via: 'Artificers’ Exchange', to: 'Gold' },
];

/**
 * Finished war components. Workshops manufacture and store these before muster.
 * `of` documents the real recipe for one component; a unit purchase spends the
 * finished component itself. It never manufactures parts out of raw stores.
 * `madeAt` names its producer, while the existing trainer gate names assembly.
 */
export interface ComponentDef {
  id: ResourceKey;
  name: string;
  flavour: string;
  /** What one of them costs, in resources. */
  of: Partial<ResourceBundle>;
  /** The yard whose benches these are cut on. */
  madeAt: string;
}

export const COMPONENTS: ComponentDef[] = [
  {
    id: 'frame',
    name: 'Frame',
    flavour: 'Seasoned, pegged, and squared by a man who will not be told about the hurry.',
    of: { planks: 5, timber: 4, tools: 0.5 },
    madeAt: 'frame_workshop',
  },
  {
    id: 'fitting',
    name: 'Fitting',
    flavour: 'Bands, pins and a winch dog. The whole engine is an argument about these.',
    of: { steel: 2, iron: 5, charcoal: 1 },
    madeAt: 'fitting_foundry',
  },
  {
    id: 'sigil_frame',
    name: 'Sigil Frame',
    flavour: 'A frame cut so the grain agrees with the writing. Both of them have to.',
    of: { frame: 1, magewood: 4, soulwood: 2 },
    madeAt: 'runic_joinery',
  },
  {
    id: 'focusing_ring',
    name: 'Focusing Ring',
    flavour: 'Turned to a thousandth. It is the thousandth that stops it going off in the yard.',
    of: { fitting: 1, alloy: 3, runestone: 3 },
    madeAt: 'lens_workshop',
  },
  {
    id: 'gilded_core',
    name: 'Gilded Core',
    flavour: 'Heartiron wound round ashglass. It is still warm when it goes in.',
    of: { fitting: 1, heartiron: 3, ashglass: 2 },
    madeAt: 'core_foundry',
  },
  {
    id: 'hollow_heart',
    name: 'Hollow Heart',
    flavour: 'It has a note, and the engine is built round keeping it.',
    of: { focusing_ring: 1, hollowglass: 3, gravesilver: 3, souls: 25 },
    madeAt: 'heart_assembly',
  },
];

const COMPONENT_BY_ID = new Map<string, ComponentDef>();
for (const c of COMPONENTS) COMPONENT_BY_ID.set(c.id, c);

export function componentDef(id: string): ComponentDef | undefined {
  return COMPONENT_BY_ID.get(id);
}

/** The stock actually consumed by assembly; raw ingredients are spent by workshops. */
export function componentCost(parts: Record<string, number>): Partial<ResourceBundle> {
  const out: Partial<ResourceBundle> = {};
  for (const id of Object.keys(parts)) {
    const def = COMPONENT_BY_ID.get(id);
    if (!def) throw new Error(`Unknown war component: ${id}`);
    const n = parts[id] ?? 0;
    if (n <= 0) continue;
    out[def.id] = (out[def.id] ?? 0) + n;
  }
  return out;
}
